import { cloneDeep } from "lodash-es";

import { Operation, Revision } from "../types/schemas";
import { transformOperation } from "./transform-operation";

/**
 * Transforms the second revision against the first revision. NOTE: Revisions must be context-equivalent.
 * Because each revision can contain multiple operations which are causally dependent on each other,
 * this transform can be thought of as rebasing rev2's operations on top of rev1's operations.
 * See ARCHITECTURE.md for rebasing explanations.
 *
 * @returns transformed second revision or null if after transformation the second revision is a noop
 */
export function transformRevision(
  rev1: Revision | null,
  rev2: Revision | null,
  priority: boolean,
): Revision | null {
  // if second revision is already a noop, it will always be a noop
  if (rev2 === null) return null;
  // if the first revision is a noop then the second revision will be unchanged
  if (rev1 === null) return rev2;

  let rev1Ops = cloneDeep(rev1.operations);
  const rev2Ops = [];

  let i = 0;
  while (i < rev2.operations.length) {
    // transform current rev2 op against all ops from rev1 (one on top of many rebase)
    let rev2Op: Operation | null = rev2.operations[i];
    let j = 0;
    while (j < rev1Ops.length) {
      const rev1Op = rev1Ops[j];
      rev2Op = transformOperation(rev1Op, rev2Op, priority);
      j += 1;
    }
    if (rev2Op) rev2Ops.push(rev2Op);

    // transform all rev1 ops against untransformed rev2 op (many on top of one rebase)
    const originalRev2Op = rev2.operations[i];
    rev1Ops = rev1Ops.reduce<Operation[]>((acc, rev1Op) => {
      const rev1OpT = transformOperation(originalRev2Op, rev1Op, !priority);
      if (rev1OpT !== null) acc.push(rev1OpT);
      return acc;
    }, []);

    i += 1;
  }

  // rev2 transformed to noop
  if (rev2Ops.length === 0) return null;

  return {
    ...rev2,
    operations: rev2Ops,
  };
}
